import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnInit,
  OnDestroy,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { MOBILE_BREAKPOINT } from 'src/app/@core/constants/breakpoints';
import { mergeClasses } from 'src/app/@core/utils/merge-classes';
import { HlmDialogContentComponent } from '../../spartan/dialog/hlm-dialog-content.component';
import { HlmDialogHeaderComponent } from '../../spartan/dialog/hlm-dialog-header.component';
import { HlmDialogFooterComponent } from '../../spartan/dialog/hlm-dialog-footer.component';
import { HlmDialogTitleDirective } from '../../spartan/dialog/hlm-dialog-title.directive';
import { HlmDialogDescriptionDirective } from '../../spartan/dialog/hlm-dialog-description.directive';

export interface ConfirmDialogContext {
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'default' | 'destructive';
  fullscreenOnMobile?: boolean;
}

/**
 * Confirm dialog component used internally by DialogService.confirm().
 * Dynamically rendered via BrnDialogService.open() — do NOT use directly.
 */
@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [
    CommonModule,
    HlmDialogContentComponent,
    HlmDialogHeaderComponent,
    HlmDialogFooterComponent,
    HlmDialogTitleDirective,
    HlmDialogDescriptionDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <hlm-dialog-content [showCloseButton]="false" [class]="contentClass()">
      <hlm-dialog-header>
        <h3 hlmDialogTitle>{{ ctx.title ?? 'Confirm' }}</h3>
        @if (ctx.description) {
          <p hlmDialogDescription [innerHTML]="ctx.description"></p>
        }
      </hlm-dialog-header>

      <hlm-dialog-footer class="mt-4">
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          (click)="onCancel()"
        >
          {{ ctx.cancelText ?? 'Cancel' }}
        </button>
        <button
          type="button"
          [class]="confirmBtnClass()"
          (click)="onConfirm()"
        >
          {{ ctx.confirmText ?? 'Confirm' }}
        </button>
      </hlm-dialog-footer>
    </hlm-dialog-content>
  `,
})
export class ConfirmDialogComponent implements OnInit, OnDestroy {
  protected readonly ctx = injectBrnDialogContext<ConfirmDialogContext>();
  private readonly _dialogRef = inject(BrnDialogRef);

  private readonly _windowWidth = signal<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200,
  );

  readonly isMobile = computed(() => this._windowWidth() < MOBILE_BREAKPOINT);

  readonly contentClass = computed(() => {
    const fullscreen = this.ctx.fullscreenOnMobile ?? true;
    if (fullscreen && this.isMobile()) {
      return '!fixed !inset-0 !w-screen !h-dvh !max-w-none !max-h-none !rounded-none !translate-x-0 !translate-y-0 !top-0 !left-0 flex flex-col';
    }
    return 'w-full max-w-md';
  });

  readonly confirmBtnClass = computed(() =>
    mergeClasses(
      'inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
      this.ctx.confirmVariant === 'destructive'
        ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
        : 'bg-primary text-primary-foreground hover:bg-primary/90',
    ),
  );

  @HostListener('window:resize')
  onResize(): void {
    this._windowWidth.set(window.innerWidth);
  }

  ngOnInit(): void {
    this._windowWidth.set(
      typeof window !== 'undefined' ? window.innerWidth : 1200,
    );
  }

  ngOnDestroy(): void {}

  onConfirm(): void {
    this._dialogRef.close(true);
  }

  onCancel(): void {
    this._dialogRef.close(false);
  }
}
